/**
 * Investees List Page — Enterprise Data Table
 *
 * Table: Name, Country, Type, Region, Staff, Borrowers, GLP, Contact
 * Create + Edit dialogs
 * API: GET /api/v1/impact-core/investees/
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  X,
  Building2,
  MapPin,
  Edit2,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Investee, InvesteeCreatePayload } from '@/types';
import { useTranslation } from '@/i18n';

export function InvesteesListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<Investee | null>(null);

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const investees = investeesQuery.data ?? [];

  const filtered = useMemo(() => {
    if (!Array.isArray(investees)) return [];
    if (!search) return investees;
    const q = search.toLowerCase();
    return investees.filter(
      (inv) =>
        (inv.name ?? '').toLowerCase().includes(q) ||
        (inv.country_code ?? inv.country ?? '').toLowerCase().includes(q) ||
        (inv.region ?? '').toLowerCase().includes(q)
    );
  }, [investees, search]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">{t('nav.investees')}</h1>
          <p className="text-xs text-ic-text-tertiary">
            {t('count.investeesTotal').replace('{count}', String(investees.length))}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('action.addInvestee')}
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
        <input
          type="text"
          placeholder={t('action.search') + '...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
        {investeesQuery.isLoading ? (
          <TableSkeleton />
        ) : investeesQuery.isError ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-red-400">{t('error.loadFailed')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-ic-text-tertiary">
              {investees.length === 0 ? t('empty.noInvesteesYet') : t('empty.noInvesteesMatch')}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs ic-table">
            <thead>
              <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                <th className="px-4 pb-2 pt-3 font-medium">{t('table.name')}</th>
                <th className="px-4 pb-2 pt-3 font-medium">{t('table.country')}</th>
                <th className="px-4 pb-2 pt-3 font-medium">{t('table.type')}</th>
                <th className="px-4 pb-2 pt-3 font-medium">{t('form.region')}</th>
                <th className="px-4 pb-2 pt-3 font-medium text-right">{t('form.staffCount')}</th>
                <th className="px-4 pb-2 pt-3 font-medium text-right">{t('form.borrowers')}</th>
                <th className="px-4 pb-2 pt-3 font-medium text-right">GLP</th>
                <th className="px-4 pb-2 pt-3 font-medium">{t('form.contactEmail')}</th>
                <th className="px-4 pb-2 pt-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50 transition-colors"
                >
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-500/10">
                        <Building2 className="h-3.5 w-3.5 text-blue-400" />
                      </div>
                      <span className="text-xs font-medium text-ic-text-primary">{inv.name}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-ic-text-tertiary" />
                      <span className="text-xs text-ic-text-secondary">{inv.country_code ?? inv.country ?? '--'}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    {inv.entity_type ? (
                      <StatusBadge label={inv.entity_type} variant="default" />
                    ) : (
                      <span className="text-xs text-ic-text-tertiary">--</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{inv.region ?? '--'}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-secondary">
                    {inv.staff_count?.toLocaleString() ?? '--'}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-secondary">
                    {inv.borrowers_count?.toLocaleString() ?? '--'}
                  </td>
                  <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-secondary">
                    {inv.glp ? `$${(inv.glp / 1_000_000).toFixed(1)}M` : '--'}
                  </td>
                  <td className="py-2.5 px-4 text-xs text-ic-text-secondary">
                    {inv.contact_email ?? inv.contact_name ?? '--'}
                  </td>
                  <td className="py-2.5 px-4">
                    <button
                      onClick={() => setEditItem(inv)}
                      className="text-ic-text-tertiary hover:text-ic-accent transition-colors"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <InvesteeFormDialog
          title={t('action.addInvestee')}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'investees'] });
          }}
        />
      )}

      {/* Edit Dialog */}
      {editItem && (
        <InvesteeFormDialog
          title={t('action.editInvestee')}
          initialData={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => {
            setEditItem(null);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'investees'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Form Dialog ───────────────────────────────────────────────

interface InvesteeFormDialogProps {
  title: string;
  initialData?: Investee;
  onClose: () => void;
  onSaved: () => void;
}

function InvesteeFormDialog({ title, initialData, onClose, onSaved }: InvesteeFormDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialData?.name ?? '');
  const [countryCode, setCountryCode] = useState(initialData?.country_code ?? initialData?.country ?? '');
  const [entityType, setEntityType] = useState(initialData?.entity_type ?? '');
  const [region, setRegion] = useState(initialData?.region ?? '');
  const [staffCount, setStaffCount] = useState(initialData?.staff_count?.toString() ?? '');
  const [borrowersCount, setBorrowersCount] = useState(initialData?.borrowers_count?.toString() ?? '');
  const [glp, setGlp] = useState(initialData?.glp?.toString() ?? '');
  const [contactEmail, setContactEmail] = useState(initialData?.contact_email ?? '');
  const [contactName, setContactName] = useState(initialData?.contact_name ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async (payload: InvesteeCreatePayload) => {
      if (initialData) {
        const res = await apiClient.put(`/api/v1/impact-core/investees/${initialData.id}`, payload);
        return res.data;
      }
      const res = await apiClient.post('/api/v1/impact-core/investees/', payload);
      return res.data;
    },
    onSuccess: () => onSaved(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } };
      const detail = axiosErr.response?.data?.detail;
      let message = t('error.operationFailed');
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map((d: { msg?: string }) => d.msg ?? String(d)).join('; ');
      } else if (detail && typeof detail === 'object') {
        message = JSON.stringify(detail);
      }
      setErrors({ _general: message });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!name.trim()) { setErrors({ name: t('error.nameRequired') }); return; }
    if (!countryCode.trim()) { setErrors({ country_code: t('error.countryCodeRequired') }); return; }

    const payload: InvesteeCreatePayload = {
      name: name.trim(),
      country_code: countryCode.trim(),
      entity_type: entityType || undefined,
      region: region || undefined,
      staff_count: staffCount ? Number(staffCount) : undefined,
      borrowers_count: borrowersCount ? Number(borrowersCount) : undefined,
      glp: glp ? Number(glp) : undefined,
      contact_email: contactEmail || undefined,
      contact_name: contactName || undefined,
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">{title}</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label={`${t('form.name')} *`} error={errors.name}>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label={`${t('form.countryCode')} *`} error={errors.country_code}>
              <input type="text" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} placeholder="e.g. KE, US"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label={t('form.entityType')}>
              <input type="text" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder="e.g. MFI, Bank, NBFI"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label={t('form.region')}>
              <input type="text" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. East Africa"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField label={t('form.staffCount')}>
              <input type="number" value={staffCount} onChange={(e) => setStaffCount(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label={t('form.borrowers')}>
              <input type="number" value={borrowersCount} onChange={(e) => setBorrowersCount(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label="GLP (USD)">
              <input type="number" value={glp} onChange={(e) => setGlp(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label={t('form.contactName')}>
              <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label={t('form.contactEmail')}>
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none" />
            </FormField>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors">
            {t('action.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors">
            {mutation.isPending ? t('action.save') + '...' : initialData ? t('action.save') : t('action.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />
      ))}
    </div>
  );
}
